import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import styles from "./Account.module.css";
import Api from "../../utils/Api/Api";
import { money, maskPhone, initialOf } from "../../utils/format";
import { endSession } from "../../utils/session";
import { isIndia } from "../../utils/market";
import { hasSession, useAuthGate } from "../../components/AuthModal/AuthGate";
import { TopBar } from "../../components/TopBar/TopBar";
import { Sheet } from "../../components/Sheet/Sheet";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { ErrorFallback } from "../../components/Error/Error";
import { GmailCard } from "../../components/GmailCard/GmailCard";
import coinLogo from "../../asset/icons/fleek-coin-logo.png";

/* The six rows the design lists. Rows without a screen behind them yet are
   present but disabled, per the decision to show every designed element. */
const ROWS = [
  { label: "My orders", to: "/myorder" },
  { label: "Fleek coins", to: null },
  { label: "Saved payment methods", to: null },
  { label: "Notifications", to: null },
  { label: "Help & support", to: "/support" },
  { label: "Terms & privacy", to: null },
];

const GENDERS = ["Male", "Female", "Other"];

// `wallet` starts as null until /wallet answers; a default parameter only
// covers undefined, so read through optional chaining.
const coinCount = (wallet) =>
  wallet?.coins ?? wallet?.total_coins ?? wallet?.coin_balance ?? wallet?.balance ?? null;

/* ---- Edit profile sheet ---- */

const EditProfileSheet = ({ open, onClose, user, onSaved }) => {
  const [form, setForm] = useState(user);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(user);
      setError("");
    }
  }, [open, user]);

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await Api.UpdateProile({
        name: form.name,
        email: form.email,
        dob: form.dob || undefined,
        gender: form.gender || undefined,
      });
      onSaved(form);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Couldn't save your details. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} label="Edit profile">
      <p className={styles.sheetTitle}>Edit profile</p>
      <p className={styles.sheetBody}>
        {isIndia() ? "Your number is your sign-in and can't be changed here." : "You sign in with Google, so your Google account can't be changed here."}
      </p>

      <div className={styles.fields}>
        <div>
          <label className={styles.fieldLabel} htmlFor="acct-name">Name</label>
          <div className={styles.field}>
            <input id="acct-name" className={styles.input} value={form.name || ""} onChange={set("name")} placeholder="Your name" />
          </div>
        </div>
        {/* India signs in by phone (+91). Elsewhere it's Google, and a phone shows only if there is one. */}
        {isIndia() ? (
          <div>
            <label className={styles.fieldLabel}>Phone</label>
            <div className={`${styles.field} ${styles.fieldDisabled}`}>
              <span className={styles.dial}>+91</span>
              <span className={styles.dialRule} aria-hidden="true" />
              <input className={styles.input} value={String(form.phone || "").replace(/\D/g, "").slice(-10)} disabled aria-label="Phone number" />
            </div>
          </div>
        ) : (
          form.phone && (
            <div>
              <label className={styles.fieldLabel}>Phone</label>
              <div className={`${styles.field} ${styles.fieldDisabled}`}>
                <input className={styles.input} value={String(form.phone)} disabled aria-label="Phone number" />
              </div>
            </div>
          )
        )}
        <div>
          <label className={styles.fieldLabel} htmlFor="acct-email">Email</label>
          <div className={styles.field}>
            <input id="acct-email" className={styles.input} type="email" value={form.email || ""} onChange={set("email")} placeholder="you@example.com" />
          </div>
        </div>
        <div>
          <label className={styles.fieldLabel} htmlFor="acct-dob">Date of birth</label>
          <div className={styles.field}>
            <input id="acct-dob" className={styles.input} type="date" value={form.dob || ""} onChange={set("dob")} />
          </div>
        </div>
        <div>
          <span className={styles.fieldLabel}>Gender</span>
          <div className={styles.chips} role="group" aria-label="Gender">
            {GENDERS.map((g) => (
              <button
                key={g}
                type="button"
                className={`${styles.chip} ${form.gender === g ? styles.chipOn : ""}`}
                aria-pressed={form.gender === g}
                onClick={() => setForm((f) => ({ ...f, gender: g }))}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <p className={styles.error} role="alert">{error}</p>}

      <button type="button" className={styles.primary} onClick={save} disabled={saving}>
        {saving ? "Saving…" : "Save changes"}
      </button>
    </Sheet>
  );
};

/* ---- Sign out sheet ---- */

const SignOutSheet = ({ open, onClose, onConfirm }) => (
  <Sheet open={open} onClose={onClose} label="Sign out">
    <p className={styles.sheetTitle}>Sign out?</p>
    <p className={styles.sheetBody}>Your orders and licence keys stay safe. Sign back in with your number any time.</p>
    <button type="button" className={`${styles.primary} ${styles.danger}`} onClick={onConfirm}>
      Sign out
    </button>
    <button type="button" className={styles.ghost} onClick={onClose}>
      Cancel
    </button>
  </Sheet>
);

/**
 * Account, rebuilt from design/Purchase Flow.dc.html (isAccount block).
 */
const AccountIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

export const Account = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState({ name: "", email: "", phone: "", dob: "", gender: "", avatar_url: "" });
  const [wallet, setWallet] = useState(null);
  const [saved, setSaved] = useState({ amount: 0, orders: 0 });
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  const { requireAuth } = useAuthGate();

  useEffect(() => {
    if (!hasSession()) {
      setLoading(false);
      requireAuth(() => window.location.reload(), "Sign in to your Fleek account.");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [profile, coins, used, orders] = await Promise.all([
          Api.GetUserDetails(),
          Api.GetFleekCoins().catch(() => null),
          Api.FleekCoinsUsed().catch(() => null),
          Api.GetOrderDetails().catch(() => null),
        ]);
        if (cancelled) return;
        const p = profile?.data || {};
        setUser({
          name: p.name || "",
          email: p.email || "",
          phone: p.phone_number || "",
          dob: p.dob || "",
          gender: p.gender || "",
          avatar_url: p.avatar_url || "",
        });
        setWallet(coins?.data || null);
        setSaved({
          amount: Number(used?.data?.sum) || 0,
          orders: orders?.data?.results?.length || 0,
        });
      } catch (err) {
        console.error("Account failed to load:", err);
        if (!cancelled) setHasError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = useCallback(() => {
    endSession("manual_logout");
    window.location.href = "/login";
  }, []);

  if (hasError) return <ErrorFallback />;

  // Signed out: the login sheet opens on arrival (see the load effect); behind
  // it, and if it is dismissed, the page follows the cart's sign-in state.
  if (!hasSession()) {
    return (
      <div className={styles.page}>
        <TopBar title="Account" backTo="/marketplace" />
        <div className={styles.shell}>
          <EmptyState
            icon={<AccountIcon />}
            title="Sign in to see your account"
            body="Your orders and coins are tied to your number."
            action="Sign in"
            onAction={() => requireAuth(() => window.location.reload(), "Sign in to your Fleek account.")}
          />
        </div>
      </div>
    );
  }

  const coins = coinCount(wallet);
  const coinWorth = wallet?.coin_value_in_inr;

  return (
    <div className={styles.page}>
      <TopBar title="Account" backTo="/marketplace" />

      <EditProfileSheet open={editing} onClose={() => setEditing(false)} user={user} onSaved={setUser} />
      <SignOutSheet open={confirmSignOut} onClose={() => setConfirmSignOut(false)} onConfirm={signOut} />

      <div className={styles.shell}>
        {loading ? (
          <>
            <div className={styles.skeleton} />
            <div className={styles.skeleton} />
            <div className={styles.skeleton} />
          </>
        ) : (
          <>
            <section className={styles.profile}>
              {user.avatar_url ? (
                <img className={styles.avatar} src={user.avatar_url} alt="" />
              ) : (
                <span className={styles.avatar} aria-hidden="true">{initialOf(user.name)}</span>
              )}
              <div className={styles.profileText}>
                <div className={styles.name}>{user.name || "Your account"}</div>
                <div className={styles.phone}>{isIndia() ? maskPhone(user.phone) : user.phone || user.email}</div>
              </div>
              <button type="button" className={styles.edit} onClick={() => setEditing(true)}>
                Edit
              </button>
            </section>

            <div className={styles.stats}>
              <div className={styles.stat}>
                <div className={styles.statHead}>
                  <img src={coinLogo} alt="" className={styles.statIcon} />
                  <span className={styles.statLabel}>Coins</span>
                </div>
                <div className={styles.statValue}>
                  {coins != null ? Number(coins).toLocaleString("en-IN") : "—"}
                </div>
                <div className={styles.statMeta}>
                  {coinWorth != null ? `worth ${money(coinWorth)}` : "worth —"}
                </div>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Saved so far</span>
                <div className={`${styles.statValue} ${styles.statValueSaved}`}>{money(saved.amount)}</div>
                <div className={styles.statMeta}>
                  across {saved.orders} {saved.orders === 1 ? "order" : "orders"}
                </div>
              </div>
            </div>

            <nav className={styles.rows} aria-label="Account">
              {ROWS.map((r) => (
                <button
                  key={r.label}
                  type="button"
                  className={styles.row}
                  disabled={!r.to}
                  aria-disabled={!r.to}
                  title={r.to ? undefined : "Coming soon"}
                  onClick={() => r.to && navigate(r.to)}
                >
                  <span className={styles.rowLabel}>{r.label}</span>
                  <span className={styles.rowChevron} aria-hidden="true">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </span>
                </button>
              ))}
            </nav>

            <GmailCard />

            <button type="button" className={styles.signOut} onClick={() => setConfirmSignOut(true)}>
              Sign out
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Account;
